import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiCalendar, FiTrendingUp, FiPieChart, FiBarChart2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import expenseService from '../services/expenseService';
import Button from '../components/Button';
import './TechnicianForm.css';

const ExpenseReports = () => {
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reports, setReports] = useState({
    byCategory: [],
    byMonth: [],
    total: { totalAmount: 0, count: 0 },
  });

  useEffect(() => {
    fetchReports();
  }, [startDate, endDate]);

  const fetchReports = async () => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await expenseService.getReports(params);
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to fetch expense reports');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getCategoryColor = (index) => {
    const colors = [
      '#3b82f6',
      '#10b981',
      '#f59e0b',
      '#ef4444',
      '#8b5cf6',
      '#ec4899',
      '#06b6d4',
      '#84cc16',
      '#f97316',
      '#6366f1',
    ];
    return colors[index % colors.length];
  };

  const getMonthName = (month) => {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return months[month - 1];
  };

  const getMaxCategoryAmount = () => {
    if (reports.byCategory.length === 0) return 1;
    return Math.max(...reports.byCategory.map((cat) => cat.totalAmount));
  };

  const getMaxMonthAmount = () => {
    if (reports.byMonth.length === 0) return 1;
    return Math.max(...reports.byMonth.map((month) => month.totalAmount));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Expense Reports</h2>
          <p className="page-subtitle">Analyze your expense trends and patterns</p>
        </div>
        <Link to="/expenses">
          <Button variant="secondary" icon={<FiArrowLeft />}>
            Back to Expenses
          </Button>
        </Link>
      </div>

      <div className="page-filters" style={{ marginBottom: '2rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FiCalendar />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="filter-select"
              placeholder="Start Date"
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FiCalendar />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="filter-select"
              placeholder="End Date"
            />
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              setStartDate('');
              setEndDate('');
            }}
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Total Summary Card */}
      <div
        style={{
          marginBottom: '2rem',
          padding: '2rem',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          color: 'white',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <p style={{ margin: 0, opacity: 0.9, fontSize: '0.875rem' }}>Total Expenses</p>
            <h2 style={{ margin: '0.5rem 0', fontSize: '2.5rem', fontWeight: 'bold' }}>
              {formatCurrency(reports.total.totalAmount)}
            </h2>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', opacity: 0.8 }}>
              {reports.total.count} expense{reports.total.count !== 1 ? 's' : ''}
            </p>
          </div>
          <div style={{ fontSize: '3rem', opacity: 0.3 }}>
            <FiTrendingUp />
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '2rem' }}>
        {/* Category-wise Summary */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <FiPieChart style={{ fontSize: '1.5rem', color: '#3b82f6' }} />
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>By Category</h3>
          </div>

          {reports.byCategory.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No expenses found</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {reports.byCategory.map((category, index) => {
                const percentage = (category.totalAmount / reports.total.totalAmount) * 100;
                const maxAmount = getMaxCategoryAmount();
                const barWidth = (category.totalAmount / maxAmount) * 100;

                return (
                  <div key={category._id || index} style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: '500', textTransform: 'capitalize' }}>
                        {category._id}
                      </span>
                      <span style={{ fontWeight: '600', color: '#1f2937' }}>
                        {formatCurrency(category.totalAmount)}
                      </span>
                    </div>
                    <div
                      style={{
                        width: '100%',
                        height: '8px',
                        background: '#e5e7eb',
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${barWidth}%`,
                          height: '100%',
                          background: getCategoryColor(index),
                          borderRadius: '4px',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      <span>{category.count} expense{category.count !== 1 ? 's' : ''}</span>
                      <span>{percentage.toFixed(1)}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Monthly Summary */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
            <FiBarChart2 style={{ fontSize: '1.5rem', color: '#10b981' }} />
            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>By Month</h3>
          </div>

          {reports.byMonth.length === 0 ? (
            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No expenses found</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {reports.byMonth.map((month, index) => {
                const maxAmount = getMaxMonthAmount();
                const barWidth = (month.totalAmount / maxAmount) * 100;
                const monthLabel = `${getMonthName(month._id.month)} ${month._id.year}`;

                return (
                  <div key={`${month._id.year}-${month._id.month}`} style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: '500' }}>{monthLabel}</span>
                      <span style={{ fontWeight: '600', color: '#1f2937' }}>
                        {formatCurrency(month.totalAmount)}
                      </span>
                    </div>
                    <div
                      style={{
                        width: '100%',
                        height: '8px',
                        background: '#e5e7eb',
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${barWidth}%`,
                          height: '100%',
                          background: '#10b981',
                          borderRadius: '4px',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      <span>{month.count} expense{month.count !== 1 ? 's' : ''}</span>
                      <span>
                        Avg: {formatCurrency(month.totalAmount / month.count || 0)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Summary Statistics */}
      {reports.byCategory.length > 0 && (
        <div
          style={{
            marginTop: '2rem',
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', fontWeight: '600' }}>Summary Statistics</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
            <div>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>Top Category</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.25rem', fontWeight: '600', textTransform: 'capitalize' }}>
                {reports.byCategory[0]?._id || 'N/A'}
              </p>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                {formatCurrency(reports.byCategory[0]?.totalAmount || 0)}
              </p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>Average per Expense</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.25rem', fontWeight: '600' }}>
                {formatCurrency(reports.total.totalAmount / reports.total.count || 0)}
              </p>
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>Categories Used</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.25rem', fontWeight: '600' }}>
                {reports.byCategory.length}
              </p>
            </div>
            {reports.byMonth.length > 0 && (
              <div>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>Highest Month</p>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.25rem', fontWeight: '600' }}>
                  {getMonthName(reports.byMonth[0]._id.month)} {reports.byMonth[0]._id.year}
                </p>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
                  {formatCurrency(reports.byMonth[0].totalAmount)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseReports;
